import { type ReactNode, useId } from 'react'
import { TagSelector } from '~/features/shared/components/tag-selector'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Textarea } from '~/shared/ui/textarea'
import { PriceLevelControl } from '../components/price-level-control'
import { HoursRows } from './hours-rows'
import { type PhotoPick, type PlaceDraft, photoSrc } from './map-place'
import { PhotoSelectGrid } from './photo-grid'

interface ReviewStepProps {
  draft: PlaceDraft
  onDraftChange: (patch: Partial<PlaceDraft>) => void
  photos: PhotoPick[]
  coverUrl: string | null
  onTogglePhoto: (url: string) => void
  onCover: (url: string) => void
}

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Card className="p-5 sm:p-6">
      {title ? (
        <h2 className="mb-4 font-semibold text-[15px] tracking-[-0.01em]">{title}</h2>
      ) : null}
      {children}
    </Card>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  className?: string
}

function Field({ label, value, onChange, type = 'text', placeholder, className }: FieldProps) {
  const id = useId()
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={id} className="font-medium text-[13px] text-muted-foreground">
        {label}
      </label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

export function ReviewStep({
  draft,
  onDraftChange,
  photos,
  coverUrl,
  onTogglePhoto,
  onCover,
}: ReviewStepProps) {
  const descriptionId = useId()
  const selectedCount = photos.filter((pick) => pick.selected).length
  const cover = photos.find((pick) => pick.selected && pick.photo.sourceUrl === coverUrl)

  return (
    <div>
      <p className="font-medium text-brand-subtle-foreground text-xs uppercase tracking-[0.14em]">
        {m.placeWebReviewEyebrow()}
      </p>
      <h1 className="mt-2 font-semibold text-2xl text-foreground tracking-[-0.025em] sm:text-[30px]">
        {m.placeWebReviewTitle()}
      </h1>
      <p className="mt-1.5 text-muted-foreground text-sm sm:text-[15px]">
        {m.placeWebReviewSubtitle()}
      </p>

      <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,1fr)_19rem] lg:items-start lg:gap-6">
        <div className="min-w-0 space-y-4">
          <Section>
            <div className="space-y-4">
              <Field
                label={m.placeWebFieldName()}
                value={draft.name}
                placeholder={m.placeWebNamePlaceholder()}
                onChange={(name) => onDraftChange({ name })}
              />
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={descriptionId}
                  className="font-medium text-[13px] text-muted-foreground"
                >
                  {m.placeWebFieldDescription()}
                </label>
                <Textarea
                  id={descriptionId}
                  value={draft.description}
                  onChange={(event) => onDraftChange({ description: event.target.value })}
                  placeholder={m.placeWebDescriptionPlaceholder()}
                  className="min-h-44 text-[15px] leading-relaxed"
                />
              </div>
            </div>
          </Section>

          <Section title={m.placeWebSectionContact()}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={m.placeWebFieldPhone()}
                type="tel"
                value={draft.phone}
                onChange={(phone) => onDraftChange({ phone })}
              />
              <Field
                label={m.placeWebFieldEmail()}
                type="email"
                value={draft.email}
                onChange={(email) => onDraftChange({ email })}
              />
              <Field
                label={m.placeWebFieldWebsite()}
                type="url"
                value={draft.website}
                onChange={(website) => onDraftChange({ website })}
                className="sm:col-span-2"
              />
              <Field
                label={m.placeWebFieldInstagram()}
                type="url"
                value={draft.instagram}
                onChange={(instagram) => onDraftChange({ instagram })}
              />
              <Field
                label={m.placeWebFieldFacebook()}
                type="url"
                value={draft.facebook}
                onChange={(facebook) => onDraftChange({ facebook })}
              />
            </div>
          </Section>

          <Section title={m.placeWebSectionAddress()}>
            <div className="grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)]">
              <Field
                label={m.placeWebFieldStreet()}
                value={draft.street}
                onChange={(street) => onDraftChange({ street })}
                className="sm:col-span-2"
              />
              <Field
                label={m.placeWebFieldPostalCode()}
                value={draft.postalCode}
                onChange={(postalCode) => onDraftChange({ postalCode })}
              />
              <Field
                label={m.placeWebFieldCity()}
                value={draft.city}
                onChange={(city) => onDraftChange({ city })}
              />
              <Field
                label={m.placeWebFieldCountry()}
                value={draft.country}
                onChange={(country) => onDraftChange({ country })}
                className="sm:col-span-2"
              />
            </div>
          </Section>

          <Section title={m.placeWebSectionHours()}>
            <HoursRows value={draft.hours} onChange={(hours) => onDraftChange({ hours })} />
          </Section>

          <Section title={m.placeWebSectionCategory()}>
            <TagSelector
              selectedCategoryId={draft.categoryId}
              selectedSubCategoryIds={draft.subCategoryIds}
              onCategoryChange={(categoryId) => onDraftChange({ categoryId, subCategoryIds: [] })}
              onSubCategoriesChange={(subCategoryIds) => onDraftChange({ subCategoryIds })}
            />
            <div className="mt-6 border-t pt-5">
              <h3 className="mb-3 font-medium text-[13px] text-muted-foreground">
                {m.listingPriceLevel()}
              </h3>
              <PriceLevelControl
                value={draft.priceLevel}
                onChange={(priceLevel) => onDraftChange({ priceLevel })}
              />
            </div>
          </Section>
        </div>

        <Card className="p-4 lg:sticky lg:top-6">
          <h2 className="font-semibold text-[15px] tracking-[-0.01em]">
            {m.placeWebSectionPhotos()}
          </h2>
          <p className="mt-0.5 mb-3 text-muted-foreground text-[13px]">
            {selectedCount > 0
              ? m.placeWebPhotosHint({ count: selectedCount })
              : m.placeWebPhotosNone()}
          </p>
          {cover ? (
            <div className="squircle relative mb-2 aspect-[4/3] overflow-hidden rounded-lg bg-muted">
              <img src={photoSrc(cover.photo)} alt="" className="size-full object-cover" />
              <span className="absolute bottom-2 left-2 rounded-full bg-card/90 px-2 py-0.5 font-medium text-[11px] text-foreground backdrop-blur-sm">
                {m.placeWebCover()}
              </span>
            </div>
          ) : null}
          <PhotoSelectGrid
            photos={photos}
            coverUrl={coverUrl}
            onToggle={onTogglePhoto}
            onCover={onCover}
          />
        </Card>
      </div>
    </div>
  )
}
